// Layout · Box
//
// Bootstrap-style surface primitive. Multi-prop closed-enum API — every
// visual decision is a named choice over a fixed vocabulary. No raw values,
// no `style`, no `padding`/`marginBottom` props.
//
// Axes:
//   - `size`   — uniform padding scale (xs..xl)
//   - `tone`   — surface color family (shared with TextValue)
//   - `border` — boolean (default soft hairline) or `"strong"`
//   - `shape`  — corner treatment (rectangle / rounded / pill)
//   - structural — grow / shrink / fullWidth / fullHeight / scroll /
//                   position / align / gridSpan
//
// For asymmetric chrome (chips, banners, divider rows), use the dedicated
// primitives (`Chip`, `Banner`, `Hairline`). Box is uniform.

import type { MouseEvent, ReactNode } from "react";
import {
  BORDER_WIDTH,
  RADIUS,
  SPACE,
  SURFACE,
  TONE,
} from "@ds/tokens/palette";
import { resolveBoxTone, type Tone } from "@ds/tokens/tones";

// ─── Axes ─────────────────────────────────────────────────────────────────

export type BoxSize  = "none" | "xs" | "sm" | "md" | "lg" | "xl";
export type BoxTone  = Tone;
export type BoxBorder = boolean | "strong";
export type BoxShape =
  | "rectangle"   // no radius
  | "rounded-sm" // RADIUS.sm  (6)
  | "rounded"    // RADIUS.md  (6) — default rounded
  | "rounded-lg" // RADIUS.lg  (8)
  | "rounded-xl" // RADIUS.xl  (12)
  | "pill";      // RADIUS.pill (999)

export type BoxScroll   = "y" | "x" | "both" | "hidden";
export type BoxPosition = "relative" | "sticky";
export type BoxAlign    = "start" | "center" | "end" | "stretch";
export type BoxGridSpan = "full";
export type BoxDividerSide = "top" | "right" | "bottom" | "left";
/** One side, or multiple sides for combined hairlines (e.g. `["top","bottom"]`). */
export type BoxDivider = BoxDividerSide | BoxDividerSide[];

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface BoxInput {
  children?: ReactNode;
  /** Uniform padding scale. Default `"md"`. */
  size?: BoxSize;
  /** Horizontal padding override. When set, wins over `size` for left/right. */
  paddingX?: BoxSize;
  /** Vertical padding override. When set, wins over `size` for top/bottom. */
  paddingY?: BoxSize;
  /** Color family (bg + text + border tint). Default `"neutral"` (no chrome). */
  tone?: BoxTone;
  /** Hairline border on all sides. `true` = soft; `"strong"` = stronger. */
  border?: BoxBorder;
  /** Hairline on one side only (e.g. row separator, banner bottom). */
  divider?: BoxDivider;
  /** Corner radius. Default `"rectangle"`. */
  shape?: BoxShape;

  /** Flex-item: grow to fill (`flex: 1`) + `minWidth: 0`. */
  grow?: boolean;
  /** Flex-item: prevent shrinking (`flexShrink: 0`). */
  shrink?: boolean;
  fullWidth?: boolean;
  /** Explicit width — px number or CSS length. For a fixed-size container
   *  (e.g. a numeric input cell) that must not collapse in an auto-layout
   *  table. Prefer `fullWidth`/`grow` for fluid layouts. */
  width?: number | string;
  /** Explicit max height — px number or CSS length. Pair with `scroll="y"`
   *  to bound a list that must stay complete but must not own the viewport
   *  (the companies map's "no coordinates" roll-call). */
  maxHeight?: number | string;
  fullHeight?: boolean;
  scroll?: BoxScroll;
  position?: BoxPosition;
  /** `alignSelf` when in a flex parent. */
  align?: BoxAlign;
  /** Grid-item span. `"full"` → `1 / -1`. */
  gridSpan?: BoxGridSpan;

  as?:
    | "div" | "span" | "section" | "article" | "aside"
    | "header" | "footer" | "nav" | "main" | "ul" | "ol" | "li" | "a";
  /** Suppress browser default list styles: margin=0, paddingLeft=0, listStyle=none.
   * Use on `as="ul"`, `as="ol"`, or `as="li"` to reset UA stylesheet without raw `style`. */
  listReset?: boolean;
  /** `href` (only meaningful when `as="a"`). Automatically suppresses browser default text-decoration. */
  href?: string;
  onClick?: (e: MouseEvent) => void;
  testId?: string;
  className?: string;
  /** DOM `id` attribute. Used for anchor targets (`scrollIntoView`, hash links). */
  id?: string;
}

export type BoxProps = BoxInput;

// ─── Token resolvers ──────────────────────────────────────────────────────

const SIZE_TO_SPACE: Record<BoxSize, number> = {
  none: 0,
  xs:   SPACE.xs,
  sm:   SPACE.sm,
  md:   SPACE.md,
  lg:   SPACE.lg,
  xl:   SPACE.xl,
};

function resolveShape(shape: BoxShape): number | undefined {
  switch (shape) {
    case "rectangle":  return undefined;
    case "rounded-sm": return RADIUS.sm;
    case "rounded":    return RADIUS.md;
    case "rounded-lg": return RADIUS.lg;
    case "rounded-xl": return RADIUS.xl;
    case "pill":       return RADIUS.pill;
  }
}

function resolveBorder(tone: BoxTone, border: BoxBorder | undefined): string | undefined {
  if (border === undefined || border === false) return undefined;
  const ramp = (TONE as Record<string, { border: string } | undefined>)[tone];
  if (ramp) return `${BORDER_WIDTH.hairline}px solid ${ramp.border}`;
  if (tone === "revenue")    return `${BORDER_WIDTH.hairline}px solid ${TONE.green.border}`;
  if (tone === "cost")       return `${BORDER_WIDTH.hairline}px solid ${TONE.red.border}`;
  if (tone === "correction") return `${BORDER_WIDTH.hairline}px solid ${TONE.amber.border}`;
  const color = border === "strong" ? SURFACE.border : SURFACE.borderSoft;
  return `${BORDER_WIDTH.hairline}px solid ${color}`;
}

// ─── Component ────────────────────────────────────────────────────────────

export function Box({
  children,
  size = "none",
  paddingX,
  paddingY,
  tone = "neutral",
  border,
  divider,
  shape = "rectangle",
  grow,
  shrink,
  fullWidth,
  width,
  maxHeight,
  fullHeight,
  scroll,
  position,
  align,
  gridSpan,
  as = "div",
  listReset,
  href,
  onClick,
  testId,
  className,
  id,
}: BoxProps) {
  const style: Record<string, unknown> = {};
  if (paddingX != null || paddingY != null) {
    const x = paddingX != null ? SIZE_TO_SPACE[paddingX] : size !== "none" ? SIZE_TO_SPACE[size] : 0;
    const y = paddingY != null ? SIZE_TO_SPACE[paddingY] : size !== "none" ? SIZE_TO_SPACE[size] : 0;
    if (y) { style.paddingTop = y; style.paddingBottom = y; }
    if (x) { style.paddingLeft = x; style.paddingRight = x; }
  } else if (size !== "none") {
    style.padding = SIZE_TO_SPACE[size];
  }

  const toneStyle = resolveBoxTone(tone);
  if (toneStyle.background) style.background = toneStyle.background;
  if (toneStyle.color)      style.color      = toneStyle.color;

  const borderStr = resolveBorder(tone, border);
  if (borderStr) style.border = borderStr;

  // Single- or multi-side hairline (orthogonal to `border`). When tone is set,
  // uses tone's border ramp; otherwise SURFACE.borderSoft.
  if (divider) {
    const t = (TONE as Record<string, { border: string } | undefined>)[tone];
    const color = t ? t.border : SURFACE.borderSoft;
    const line  = `${BORDER_WIDTH.hairline}px solid ${color}`;
    const sides = Array.isArray(divider) ? divider : [divider];
    if (sides.includes("top"))    style.borderTop    = line;
    if (sides.includes("right"))  style.borderRight  = line;
    if (sides.includes("bottom")) style.borderBottom = line;
    if (sides.includes("left"))   style.borderLeft   = line;
  }

  const radius = resolveShape(shape);
  if (radius !== undefined) style.borderRadius = radius;

  if (grow)                { style.flex       = "1 1 0"; style.minWidth = 0; }
  if (shrink)              style.flexShrink = 0;
  if (fullWidth)           style.width      = "100%";
  if (width !== undefined) style.width      = width;
  if (maxHeight !== undefined) style.maxHeight = maxHeight;
  if (fullHeight)          style.height     = "100%";
  if (scroll === "y")      style.overflowY  = "auto";
  if (scroll === "x")      style.overflowX  = "auto";
  if (scroll === "both")   style.overflow   = "auto";
  if (scroll === "hidden") style.overflow   = "hidden";
  if (position)            style.position   = position;
  if (align === "start")   style.alignSelf  = "flex-start";
  if (align === "end")     style.alignSelf  = "flex-end";
  if (align === "center" || align === "stretch") style.alignSelf = align;
  if (gridSpan === "full") style.gridColumn = "1 / -1";

  if (listReset) {
    style.margin      = 0;
    style.paddingLeft = 0;
    style.listStyle   = "none";
  }

  if (onClick) {
    style.cursor     = "pointer";
    style.userSelect = "none";
  }

  // When rendered as an anchor, suppress the browser's default blue underline.
  if (as === "a") style.textDecoration = style.textDecoration ?? "none";

  const Tag = as;
  const extraProps: Record<string, unknown> = {};
  if (as === "a" && href !== undefined) extraProps.href = href;

  return (
    <Tag
      id={id}
      data-testid={testId}
      className={className}
      style={style}
      onClick={onClick}
      {...extraProps}
    >
      {children}
    </Tag>
  );
}
